import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { SIGNATURE_OFFER as O } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";

export const SignatureOffer = () => (
  <section data-testid="offer-section" id="offer" className="relative py-28 lg:py-40 overflow-hidden">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[700px] rounded-full bg-gold/5 blur-[140px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="relative border border-gold/25 rounded-[2rem] bg-charcoal-800/50 backdrop-blur-sm p-8 sm:p-12 lg:p-16 overflow-hidden">
        <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-transparent via-gold to-transparent" />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — the offer */}
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow testid="offer-eyebrow">{O.tag}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.02]">
                {O.name.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="italic text-gold-gradient">{O.name.split(" ").slice(-1)}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-cream/60 mt-6 text-base sm:text-lg leading-relaxed">{O.promise}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 space-y-3">
                {O.phases.map((p, i) => (
                  <div key={i} data-testid={`offer-phase-${i}`} className="flex gap-5 items-start border-l border-gold/30 pl-5 py-1">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-gold whitespace-nowrap pt-1 w-24 shrink-0">{p.k}</span>
                    <div>
                      <p className="font-serif text-xl text-cream">{p.t}</p>
                      <p className="text-cream/50 text-sm leading-relaxed mt-0.5">{p.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right — outcomes + CTA */}
          <div className="lg:col-span-6 lg:border-l lg:border-white/10 lg:pl-16">
            <Reveal delay={0.2}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 mb-6">What you walk away with</p>
              <ul className="space-y-4">
                {O.outcomes.map((o, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="flex gap-3 text-cream/80"
                  >
                    <span className="h-6 w-6 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={13} className="text-gold" />
                    </span>
                    <span className="text-sm sm:text-base leading-relaxed">{o}</span>
                  </motion.li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-10 pt-8 border-t border-white/10">
                <p className="text-gold text-sm mb-5">{O.price}</p>
                <button
                  data-testid="offer-cta"
                  onClick={() => document.querySelector("#schedule")?.scrollIntoView({ behavior: "smooth" })}
                  className="group w-full sm:w-auto px-9 py-4 rounded-full bg-gold text-charcoal font-semibold flex items-center justify-center gap-2 hover:bg-gold-light transition-all hover:scale-[1.02]"
                >
                  Apply for a sprint
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);
